import { useTranslation } from "react-i18next"
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  NotebookPen,
  ClipboardList,
  Receipt,
  Search,
  Sparkles,
  CheckCircle2,
} from "lucide-react"
import { useBrand } from "@/hooks/use-brand"

const NAV_ICONS = [
  LayoutDashboard,
  Users,
  CalendarDays,
  NotebookPen,
  ClipboardList,
  Receipt,
]

export function HeroDashboardPreview({ ariaLabel }) {
  const { t } = useTranslation()
  const brand = useBrand()
  const navLabels = t("landing.preview.nav", { returnObjects: true })
  const appointments = t("landing.preview.appointments", { returnObjects: true })
  const stats = t("landing.preview.stats", { returnObjects: true })

  const labels = Array.isArray(navLabels) ? navLabels : []
  const appts = Array.isArray(appointments) ? appointments : []
  const statCards = Array.isArray(stats) ? stats : []

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      className="aspect-[16/10] w-full min-h-[300px] bg-gradient-to-br from-background via-muted/30 to-secondary/20 flex overflow-hidden text-left"
    >
      <aside className="hidden sm:flex w-[148px] shrink-0 flex-col border-r border-border bg-card/80">
        <div className="h-11 px-3 flex items-center gap-2 border-b border-border">
          <span className="h-6 w-6 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-bold text-[10px] shrink-0">
            A
          </span>
          <span className="font-display text-[10px] tracking-tight truncate">{brand.brand}</span>
        </div>
        <div className="px-3 py-2.5 border-b border-border">
          <p className="text-[8px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
            {t("landing.preview.practiceLabel")}
          </p>
          <p className="mt-0.5 text-[10px] font-medium truncate">{t("landing.preview.practiceName")}</p>
        </div>
        <nav className="flex-1 px-2 py-2 space-y-0.5">
          {labels.map((label, i) => {
            const Icon = NAV_ICONS[i] || LayoutDashboard
            const active = i === 0
            return (
              <div
                key={label}
                className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] ${
                  active
                    ? "bg-secondary text-secondary-foreground font-medium"
                    : "text-muted-foreground"
                }`}
              >
                <Icon strokeWidth={1.5} className="h-3 w-3 shrink-0" />
                <span className="truncate">{label}</span>
              </div>
            )
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="h-11 shrink-0 border-b border-border bg-muted/40 flex items-center gap-2 px-3 sm:px-4">
          <div className="flex-1 flex items-center gap-2 rounded-md border border-border bg-background/80 px-2.5 py-1 max-w-xs">
            <Search strokeWidth={1.5} className="h-3 w-3 text-muted-foreground shrink-0" />
            <span className="text-[10px] text-muted-foreground truncate">{t("landing.preview.searchPlaceholder")}</span>
          </div>
          <div className="h-7 w-7 rounded-full bg-secondary text-secondary-foreground grid place-items-center text-[9px] font-semibold shrink-0">
            DP
          </div>
        </div>

        <div className="flex-1 p-3 sm:p-4 overflow-hidden">
          <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
            <div>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{t("landing.preview.todayLabel")}</p>
              <h3 className="font-display text-sm sm:text-base tracking-tight font-medium mt-0.5">
                {t("landing.preview.greeting")}
              </h3>
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums">{t("landing.preview.date")}</span>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-3">
            {statCards.map((s) => (
              <div key={s.label} className="rounded-lg border border-border bg-card/90 px-2.5 py-2">
                <p className="text-[9px] text-muted-foreground truncate">{s.label}</p>
                <p className="font-display text-lg tracking-tight font-medium mt-0.5 tabular-nums">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 sm:gap-3">
            <div className="sm:col-span-2 rounded-xl border border-border bg-card/90 p-2.5 sm:p-3 flex flex-col min-h-[120px]">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[10px] font-medium">{t("landing.preview.scheduleTitle")}</p>
                <span className="text-[9px] rounded-full bg-secondary px-2 py-0.5 text-secondary-foreground">
                  {appts.length} {t("landing.preview.sessions")}
                </span>
              </div>
              <ul className="space-y-1.5 flex-1">
                {appts.map((a) => (
                  <li
                    key={`${a.time}-${a.name}`}
                    className="flex items-center gap-2 rounded-md border border-border/60 bg-background/60 px-2 py-1.5"
                  >
                    <span className="text-[9px] font-mono text-muted-foreground w-9 shrink-0 tabular-nums">{a.time}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-medium truncate">{a.name}</p>
                      <p className="text-[9px] text-muted-foreground truncate">{a.type}</p>
                    </div>
                    {a.status === "done" ? (
                      <CheckCircle2 strokeWidth={1.5} className="h-3 w-3 text-accent shrink-0" />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="sm:col-span-3 rounded-xl border border-border bg-card/90 p-2.5 sm:p-3 flex flex-col min-h-[120px]">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[10px] font-medium">{t("landing.preview.noteTitle")}</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 text-accent px-2 py-0.5 text-[9px] font-medium">
                  <Sparkles strokeWidth={1.5} className="h-2.5 w-2.5" />
                  {t("landing.preview.aiDraft")}
                </span>
              </div>
              <div className="flex-1 space-y-2 text-[9px] sm:text-[10px] leading-relaxed text-muted-foreground">
                <p>
                  <span className="font-semibold text-foreground">{t("landing.preview.soapS")}: </span>
                  {t("landing.preview.soapSBody")}
                </p>
                <p>
                  <span className="font-semibold text-foreground">{t("landing.preview.soapO")}: </span>
                  {t("landing.preview.soapOBody")}
                </p>
                <p>
                  <span className="font-semibold text-foreground">{t("landing.preview.soapA")}: </span>
                  {t("landing.preview.soapABody")}
                </p>
              </div>
              <div className="mt-2 pt-2 border-t border-border flex items-center justify-between gap-2">
                <span className="text-[9px] text-muted-foreground">{t("landing.preview.noteMeta")}</span>
                <span className="text-[9px] rounded-md bg-primary text-primary-foreground px-2 py-1 font-medium">
                  {t("landing.preview.signCta")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}